import { CanMatchFn, Route, Router, UrlSegment, UrlTree } from '@angular/router';
import { inject } from '@angular/core';

import { OasisAuthService } from './oasis-auth.service';
import { PortalAvailabilityService } from '../cycle/portal-availability';

/**
 * Route guard for authenticated areas (the dashboard and the application form).
 *
 * When there's no valid access token we capture where the visitor was heading
 * and kick off the Cognito Hosted UI sign-in flow, blocking this navigation.
 * The target rides along in the OAuth `state` parameter so the post-login page
 * can resume the applicant exactly where they left off rather than always
 * dropping them on the dashboard — see `OasisAuthService.consumePostLoginTarget`.
 *
 * Before the portal opens, an unauthenticated deep link goes to the landing page
 * instead of the Hosted UI: otherwise a stale bookmark or a shared link is enough
 * to reach Cognito's sign-up screen and create an account the portal isn't
 * offering yet. Already-authenticated visitors are let through untouched — the
 * server still refuses saves outside the submission window.
 */
export const authGuard: CanMatchFn = (_route: Route, segments: UrlSegment[]): boolean | UrlTree => {
	const auth = inject(OasisAuthService);
	if (auth.isAuthenticated()) return true;

	const router = inject(Router);
	if (!inject(PortalAvailabilityService).isOpen()) return router.parseUrl('/');

	// Prefer the full in-flight URL so query params and fragments survive the
	// round trip; `segments` covers only this route's own path segments.
	const inFlight = router.getCurrentNavigation()?.extractedUrl.toString();
	const target = inFlight ?? '/' + segments.map((s) => s.path).join('/');

	auth.login(target);
	return false;
};
